// IMP-137: 공용 Customer Licenses 테이블 컴포넌트.
// LicenseSubsidiaryTab.js / DealerIssuePanel.js 양쪽에서 import 하여 사용.
//
// 책임 분리:
//   parent  : 필터 state 보유 + BE refetch + FE-side 추가 필터링.
//   본 컴포넌트: 헤더(타이틀/필터 UI 컨트롤) + 테이블(thead/tbody) 렌더만 담당.
//
// 필터 controlled 패턴 — 부모가 값/onChange/options 를 props 로 전달.
//
// IMP-149 흡수: 발급일(Period/Issued At) PeriodFilter 를 양 탭에서 일관되게 표시.
//   periodFilter / onPeriodChange 미전달 시 발급일 필터 미표시 (옵트인).
//
// IMP-150: Customer Licenses 테이블의 User ID 셀은 groupName 우선 + email 별도 줄
//   (LicenseDetailModal 도 동일 패턴 적용).
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PeriodFilter, { FILTER_ROW_CLASS } from './PeriodFilter';
import { Translate as t } from '../../config/Translate';
import { renderHeader } from './columns';
import CompanyCell from './CompanyCell';
import useClientTable from './useClientTable';
import TablePager from './TablePager';
import SortableHeader from './SortableHeader';
import { formatLicenseDate, getFriendlyName, getUserEmail, isRenewalLicense, isExpired } from '../../lib/licenseHelper';
import { LICENSE_STATUS_SUSPEND } from '../../config/appConstants';

// voucher_license.status / suspended / expired 기반 표시 status 산출.
//   API status values (MongoDB SSoT): 'active' | 'actived' | 'suspend' | 'resume'
//   computed UI values             : 'active' | 'suspended' | 'expired'
function computeLicStatus(lic) {
    if (lic?.status === LICENSE_STATUS_SUSPEND || lic?.suspended === true) return 'suspended';
    if (isExpired(lic)) return 'expired';
    return 'active';
}

function statusLabelOf(statusKey) {
    if (statusKey === 'suspended') return 'Suspended';
    if (statusKey === 'expired') return 'Expired';
    return 'Active';
}

// PR #215 review (CQ Minor) — colSpan 매직 넘버 제거. 헤더 배열에서 동적 계산.
// 컬럼 추가/삭제 시 본 배열만 갱신하면 thead/empty colSpan 모두 자동 정합.
// IMP-163 (2026-05-08): Dealer → Company 통일 + 컬럼 폭/순서 조정.
//   - 폭: Expires/Issued At 좁게(시간 표시 폭만), Memo 넓게
//   - 순서: User ID → Company → Type → Status → Issued At → Expires → Memo → Actions
//     "Who(User+Company) → What(Type) → State(Status) → When(Issued→Expires) → Notes → Action"
//     Issued At → Expires 는 시간 흐름순. 법인/딜러 양 탭 공용.
// COLUMN_HEADERS 를 객체 배열로 변경 — 컬럼별 폭/wrap 정책을 데이터로 표현.
// IMP-167: key = 안정 식별자, label = i18n 키 (기존 key 값과 동일), style 유지.
// IMP-437: Type/Status/Memo 3개만 dotted 키로 교체 — 19개 언어 전부에 flat 키('Type' 등)가
// 없어 미번역 노출되던 것을 해소. 형제(User ID/Company/Issued At/Expires)는 flat 키가 19/19
// 존재해 그대로 둔다(기존 키 재사용 — 신규 키 생성 회피).
// sortable: true 인 열만 머리 클릭 정렬(원값은 getSortValue 가 key 로 돌려준다).
const COLUMN_HEADERS = [
    { key: 'userId', label: 'User ID', sortable: true },
    { key: 'company', label: 'Company', sortable: true },
    { key: 'type', label: 'common.field.type', sortable: true, style: { whiteSpace: 'nowrap' } },
    { key: 'status', label: 'common.label.status', sortable: true, style: { whiteSpace: 'nowrap' } },
    { key: 'issuedAt', label: 'Issued At', sortable: true, style: { width: 150, whiteSpace: 'nowrap' } },
    { key: 'expires', label: 'Expires', sortable: true, style: { width: 150, whiteSpace: 'nowrap' } },
    // PR #220 review #11: 헤더 + 셀 폭 정책 일치 (maxWidth 헤더에도 명시 — 헤더가 SoT).
    { key: 'memo', label: 'common.field.memo', style: { minWidth: 280, maxWidth: 400 } },
];

// 날짜 원값 → 타임스탬프 숫자(파싱 불가는 null → 정렬 시 항상 뒤).
const toTime = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = new Date(v).getTime();
    return Number.isNaN(n) ? null : n;
};

const DEFAULT_SORT = { key: 'issuedAt', dir: 'desc' };

const CustomerLicensesTable = ({
    // Data
    licenses = [],
    companyMap = {},

    // Permissions
    canEdit = true,
    // 상세 열기 가능 여부 — 미전달 시 canEdit 를 따른다(기존 호출부 동작 유지).
    showDetail,

    // Callbacks
    onDetail,

    // Filters — 모두 controlled. 미전달 시 해당 필터 컨트롤 미표시.
    periodFilter,           // {preset, from, to} | undefined → 미표시
    onPeriodChange,
    // 부모가 기간 필터를 상단에서 소유하면 true — 이 표 안의 발급일 컨트롤을 그리지 않는다(기본 false).
    hidePeriodFilter = false,
    // 기간 바로 뒤(유형 앞)에 놓을 추가 컨트롤(예: 법인·하위 조직 콤보). note: 줄 맨 끝 작은 안내.
    extraFilters = null,
    note = null,
    statusFilter,           // string | undefined → 미표시
    statusOptions,          // [{value, label}] — 부모가 케이스 정의
    onStatusChange,
    typeFilter,             // string | undefined → 미표시
    typeOptions,            // [{value, label}]
    onTypeChange,
    expiresFilter,          // {preset, from, to} | undefined → 미표시
    onExpiresChange,
    // IMP-194: User ID / Name / Email 부분 일치 검색 (controlled).
    searchFilter,           // string | undefined → 미표시
    onSearchChange,
}) => {
    // IMP-204: 로컬 입력 state — 키입력마다 화면 즉시 반영, 부모 호출은 300ms debounce.
    const [searchInput, setSearchInput] = useState(searchFilter ?? '');

    // 외부 prop 동기화 (부모가 searchFilter를 리셋할 때 input도 동기화).
    // searchFilter 미전달(undefined) 경로에서는 검색 input 자체가 미렌더이므로
    // 로컬 state를 건드리지 않는다 (불필요한 setState 회피).
    useEffect(() => {
        if (searchFilter !== undefined) setSearchInput(searchFilter);
    }, [searchFilter]);

    // onSearchChange 최신 참조를 ref로 유지 — 타이머를 재생성하지 않으면서
    // 항상 최신 콜백을 호출할 수 있게 함.
    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => { onSearchChangeRef.current = onSearchChange; }, [onSearchChange]);

    // debounce 타이머 ref — setTimeout/clearTimeout 직접 사용.
    // jest fake timers(setTimeout mock)와 완전 호환됨.
    const debounceTimerRef = useRef(null);

    const handleSearchInput = useCallback((v) => {
        setSearchInput(v);
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            if (onSearchChangeRef.current) onSearchChangeRef.current(v);
        }, 300);
    }, []);

    // 언마운트 시 pending 타이머 취소.
    useEffect(() => () => {
        if (debounceTimerRef.current !== null) clearTimeout(debounceTimerRef.current);
    }, []);

    const detailEnabled = showDetail ?? canEdit;
    const showActions = canEdit || detailEnabled;

    const safeLicenses = useMemo(() => (Array.isArray(licenses) ? licenses : []), [licenses]);
    const getSortValue = useCallback((lic, key) => {
        switch (key) {
            case 'userId': return (getFriendlyName(lic) || '').toString().toLowerCase();
            case 'company': {
                const c = lic?.dealerId ? companyMap?.[lic.dealerId] : null;
                return (c?.name || lic?.dealerId || '').toString().toLowerCase();
            }
            case 'type': return isRenewalLicense(lic) ? 1 : 0;
            case 'status': return computeLicStatus(lic);
            case 'issuedAt': return toTime(lic?.created ?? lic?.issuedAt);
            case 'expires': return toTime(lic?.expired);
            default: return null;
        }
    }, [companyMap]);
    const {
        pageRows, sortKey, sortDir, toggleSort, page, setPage, pageSize, setPageSize, total,
    } = useClientTable(safeLicenses, { getSortValue, defaultSort: DEFAULT_SORT });

    const headerCols = COLUMN_HEADERS.map((c) => {
        if (!c.sortable) return c;
        const arrow = sortKey === c.key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
        return {
            ...c,
            node: (
                <SortableHeader
                    label={c.label}
                    active={sortKey === c.key}
                    dir={sortDir}
                    onToggle={() => toggleSort(c.key)}
                />
            ),
        };
    });

    const colCount = showActions ? COLUMN_HEADERS.length + 1 : COLUMN_HEADERS.length;

    return (
        <>
            {/* 필터 줄 — 발급 이력과 같은 공용 클래스·순서(기간 → 유형 → 상태 → 만료 → 검색). 탭 이름이 곧 표 제목이라 별도 제목은 없다. */}
            <div className={FILTER_ROW_CLASS}>
                <>
                    {!hidePeriodFilter && periodFilter !== undefined && onPeriodChange && (
                        <PeriodFilter value={periodFilter} onChange={onPeriodChange} />
                    )}
                    {extraFilters}
                    {typeFilter !== undefined && onTypeChange && Array.isArray(typeOptions) && (
                        <select
                            className="form-select form-select-sm w-auto"
                            value={typeFilter}
                            onChange={(e) => onTypeChange(e.target.value)}
                            aria-label={t('common.field.type')}
                        >
                            {typeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    )}
                    {statusFilter !== undefined && onStatusChange && Array.isArray(statusOptions) && (
                        <select
                            className="form-select form-select-sm w-auto"
                            value={statusFilter}
                            onChange={(e) => onStatusChange(e.target.value)}
                            aria-label={t('common.label.status')}
                        >
                            {statusOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    )}
                    {expiresFilter !== undefined && onExpiresChange && (
                        <PeriodFilter value={expiresFilter} onChange={onExpiresChange} label="Expires" />
                    )}
                    {searchFilter !== undefined && onSearchChange && (
                        <input
                            type="text"
                            className="form-control form-control-sm w-auto"
                            value={searchInput}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            placeholder={t('user.label.searchByUserIdNameEmail')}
                            aria-label={t('user.label.searchByUserIdNameEmail')}
                            style={{ minWidth: 220 }}
                        />
                    )}
                </>
                {note && <div className="ms-auto d-flex align-items-center gap-2">{note}</div>}
            </div>

            <div className="mb-2">
            <div className="table-responsive">
                <table className="table table-sm table-bordered">
                    <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                        <tr>
                            {renderHeader(headerCols)}
                            {showActions && <th style={{ whiteSpace: 'nowrap' }}>{t('common.label.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody className="align-middle">
                        {total === 0 ? (
                            <tr>
                                <td colSpan={colCount} className="text-center text-muted">
                                    {t('common.message.noData')}
                                </td>
                            </tr>
                        ) : (
                            pageRows.map((lic) => {
                                const statusKey = computeLicStatus(lic);
                                // 키: lic._id + sk 조합 — 같은 _id 가 재사용되는 voucher_license 도 안전.
                                const rowKey = `${lic._id?.toString?.() ?? lic._id ?? ''}:${lic.sk ?? ''}`;
                                return (
                                    <tr key={rowKey}>
                                        <td>
                                            <div>{getFriendlyName(lic)}</div>
                                            {getUserEmail(lic) && <div className="small text-muted">{getUserEmail(lic)}</div>}
                                            {/* FEAT-032 (BE PR #118, 2026-05-14): voucher_license.phoneNumber
                                                필드 추가에 따라 표시. email 과 동일 스타일 (small text-muted). */}
                                            {lic.phoneNumber && (
                                                <div className="small text-muted">{lic.phoneNumber}</div>
                                            )}
                                        </td>
                                        {/* IMP-163: 컬럼 순서 — Company → Type → Status → Issued At → Expires → Memo.
                                            Sub-org Allocations / AllocationHistoryTable 패턴 (회사명 + 보조 ID). */}
                                        <td>
                                            <CompanyCell id={lic.dealerId} companyMap={companyMap} fallback="-" />
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{isRenewalLicense(lic) ? t('license.label.renewal') : t('license.label.initial')}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{t(statusLabelOf(statusKey))}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatLicenseDate(lic.created ?? lic.issuedAt)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatLicenseDate(lic.expired)}</td>
                                        <td className="small text-muted" style={{ minWidth: 280, maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {(typeof lic.memo === 'string' && lic.memo.trim().length > 0) ? lic.memo : '-'}
                                        </td>
                                        {showActions && (
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {/* RTSQA-339 round 14 (사용자 발견 2026-05-14): list 액션
                                                    버튼 정렬 보정 — margin-left 1px. */}
                                                {detailEnabled && (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary py-0"
                                                    style={{ fontSize: '0.75rem', margin: '0 0 0 1px' }}
                                                    onClick={() => onDetail?.(lic)}
                                                >
                                                    {t('common.label.detail')}
                                                </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <TablePager
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />
            </div>
        </>
    );
};

export default CustomerLicensesTable;
